import React, { useMemo } from "react";
import { AgCharts } from "ag-charts-react";
import Text from "../../../common/typography/text.js";
import Button from "../../../common/button/button.js";
import ChangeIndicator from "../../../common/change-indicator/change-indicator.js";
import {
  formatCurrency,
  formatUOM,
  formatGrowth,
  growthVariant,
} from "../aabiFormatters";
import {
  AABI_DONUT_PALETTE,
  getAabiChartBaseOptions,
  prefersReducedMotion,
  AABI_NUMBER_BIG_LABEL,
} from "../aabiChartOptions";
import { MEASURE_LABELS } from "../aabiConstants";
import { formatCompactCurrency } from "../aabiViewModel";
import CompactCurrency from "./CompactCurrency";
import RankedListSkeleton from "./RankedListSkeleton.js";
import "./../AABI.css";

// One slot of the portfolio donut cascade (§6.2): a donut of the selected
// measure by contributor with the total in the centre, plus the
// keyboard/touch-accessible ranked list that doubles as its legend (§9.4,
// §10.3). Selecting a slice or a list row drives the next slot.
//
// A slot only draws its ring once it has data. While it is waiting on a
// selection it shows the hint that says what unlocks it — the skeleton ring
// it used to show implied a request was in flight when nothing was loading,
// and left most of the panel as decoration on first paint.
const PortfolioDonut = ({
  title,
  subtitle,
  portfolioRows,
  totalValue,
  measure,
  activeId,
  onSelect,
  onInvoiceRow,
  status,
  pendingHint,
  onRetry,
  // True when the rows are buttons or links: a CompactCurrency toggle button
  // nested inside would be invalid interactive content, so figures render as
  // static text (the exact value stays in the tooltip). Default keeps the
  // manufacturer page's click-to-expand figures.
  staticValues = false,
  // Show the "View invoices" action on every row, not just product rows. The
  // Executive Dashboard needs it on category rows, whose click selects them.
  invoiceActionOnAllRows = false,
  // Draw the invoice action as a compact top-right arrow icon button instead of
  // the "View invoices" text link.
  invoiceActionAsIcon = false,
}) => {
  const measureLabel = MEASURE_LABELS[measure] || "Revenue";

  const chartData = useMemo(
    () =>
      portfolioRows.map((row) => ({
        id: row.id,
        name: row.code ? `${row.name} (${row.code})` : row.name,
        value: row.selectedRevenue,
        shareLabel: `${(row.selectedShare * 100).toFixed(1)}%`,
        row,
      })),
    [portfolioRows],
  );

  const chartOptions = useMemo(() => {
    if (!chartData.length) return null;
    const base = getAabiChartBaseOptions({
      reducedMotion: prefersReducedMotion(),
    });
    return {
      ...base,
      data: chartData,
      series: [
        {
          type: "donut",
          angleKey: "value",
          legendItemKey: "name",
          calloutLabel: { enabled: false },
          // No sector labels: at this ring thickness AG Charts drops them
          // anyway, so the config was dead and the percentages only ever
          // appeared in the ranked list beside the ring.
          sectorLabel: { enabled: false },
          innerRadiusRatio: 0.62,
          innerLabels: [
            {
              text: formatCompactCurrency(totalValue),
              ...AABI_NUMBER_BIG_LABEL,
            },
            { text: `Total ${measureLabel}`, fontSize: 10 },
          ],
          fills: AABI_DONUT_PALETTE,
          strokes: ["#FFFFFF"],
          strokeWidth: 2,
          cursor: "pointer",
          itemStyler: ({ datum }) =>
            activeId != null && datum.id !== activeId
              ? { fillOpacity: 0.35 }
              : undefined,
          tooltip: {
            // AG Charts v11's AgTooltipRendererResult is { heading, title,
            // data: [{ label, value }] } — there is no `content` field. A
            // renderer returning one silently falls through to the default
            // body, which prints the raw angleKey number.
            renderer: ({ datum }) => ({
              title: datum.name,
              data: [
                { label: measureLabel, value: formatCurrency(datum.value) },
                { label: "Share of level", value: datum.shareLabel },
              ],
            }),
          },
          listeners: {
            nodeClick: ({ datum }) => {
              // Rows with a null id ("Uncategorized" / "Ungrouped") cannot be drilled.
              if (datum?.row && !datum.row.isLeaf && datum.row.id != null) onSelect?.(datum.row);
            },
          },
        },
      ],
      legend: { enabled: false },
    };
  }, [chartData, totalValue, measureLabel, activeId, onSelect]);

  const isReady = status === "ready" && portfolioRows.length > 0;

  const renderRow = (row) => {
    const isActive = activeId != null && row.id === activeId;
    const isProduct = Boolean(row.isLeaf);
    // Backend emits id: null for lines with no category/group/product; such rows are
    // informational only (counted in totals, not drillable, no invoice handoff).
    const isSelectable = !isProduct && row.id != null && Boolean(onSelect);
    const growth = row.growth;

    const rowContent = (
      <>
        <div className="aabi-ranked-row-main">
          <Text variant="tinyText" color="darkText" className="aabi-ranked-name">
            {row.code ? `${row.name} (${row.code})` : row.name}
          </Text>
          <Text variant="tinyText" color="lightText">
            {`${(row.selectedShare * 100).toFixed(1)}%`}
          </Text>
        </div>
        <div className="aabi-ranked-bar-track" aria-hidden="true">
          {/* Exactly the share printed beside it. The old 2x scaling drew a
              45.1% row at 90% and pinned anything over half to full width,
              so the bar contradicted its own number. */}
          <span
            className="aabi-ranked-bar"
            style={{ width: `${Math.min(100, row.selectedShare * 100)}%` }}
          />
        </div>
        <div className="aabi-ranked-row-secondary">
          <CompactCurrency
            value={row.selectedRevenue}
            variant="tinyText"
            color="darkText"
            staticValue={staticValues && isSelectable}
          />
          <div className="aabi-ranked-meta">
            <Text variant="tinyText" color="lightText">
              {`UOM ${formatUOM(row.uom)}`}
            </Text>
            <ChangeIndicator
              variant={growthVariant(growth)}
              percent={formatGrowth(growth)}
            />
          </div>
        </div>
      </>
    );

    return (
      <li key={`${row.id}-${row.name}`} className="aabi-ranked-item">
        {isSelectable ? (
          <button
            type="button"
            className={`aabi-ranked-row${isActive ? " aabi-ranked-row-active" : ""}`}
            aria-pressed={isActive}
            onClick={() => onSelect(row)}
          >
            {rowContent}
          </button>
        ) : (
          <div
            className={`aabi-ranked-row${isActive ? " aabi-ranked-row-active" : ""}`}
          >
            {rowContent}
          </div>
        )}
        {(isProduct || invoiceActionOnAllRows) && row.id != null && onInvoiceRow && (
          invoiceActionAsIcon ? (
            <Button
              variant="icon"
              className="aabi-row-action-icon"
              aria-label={`View invoices for ${row.code ? `${row.name} (${row.code})` : row.name}`}
              title="View invoices"
              onClick={(event) => {
                event.stopPropagation();
                onInvoiceRow(row);
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4.5 11.5L11.5 4.5" />
                <path d="M5.5 4.5H11.5V10.5" />
              </svg>
            </Button>
          ) : (
            <Button
              variant="link"
              onClick={(event) => {
                event.stopPropagation();
                onInvoiceRow(row);
              }}
            >
              <Text variant="smallText" color="primaryColor">
                View invoices
              </Text>
            </Button>
          )
        )}
        {(isProduct || invoiceActionOnAllRows) && row.id == null && onInvoiceRow && invoiceActionAsIcon && (
          // Keep informational rows aligned with rows that have an invoice action.
          <span className="aabi-row-action-spacer" aria-hidden="true" />
        )}
      </li>
    );
  };

  return (
    <section
      className="aabi-donut-slot"
      aria-label={title}
      aria-busy={status === "loading"}
    >
      <div className="aabi-donut-slot-header">
        <Text variant="smallText" color="darkText">
          {title}
        </Text>
        {subtitle && (
          <Text variant="tinyText" color="lightText">
            {subtitle}
          </Text>
        )}
      </div>

      {status === "error" ? (
        <div role="alert" className="aabi-panel-error">
          <Text variant="tinyText" color="failureColor">
            Couldn&apos;t load this breakdown.
          </Text>
          {onRetry && (
            <Button variant="primary" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      ) : status === "ready" && portfolioRows.length === 0 ? (
        <div className="aabi-empty-state aabi-empty-state-compact">
          <Text variant="tinyText" color="lightText">
            No activity in this period.
          </Text>
        </div>
      ) : status === "pending" ? (
        // A waiting slot says what unlocks it. It no longer shows a skeleton
        // ring: nothing is loading, so an animated placeholder implied work
        // that was not happening.
        <div className="aabi-slot-pending">
          {pendingHint && (
            <Text variant="tinyText" color="lightText">
              {pendingHint}
            </Text>
          )}
        </div>
      ) : !isReady ? (
        <RankedListSkeleton rows={3} />
      ) : (
        <>
          {chartOptions && (
          <div
            className="aabi-chart-frame"
            role="img"
            aria-label={`${title} donut of ${measureLabel.toLowerCase()} by contributor. Use the ranked list below to select a section.`}
          >
            <AgCharts
              options={chartOptions}
              style={{ width: "100%", height: 220 }}
            />
          </div>
          )}
          <ol className="aabi-ranked-list" aria-label={`${title} ranked list`}>
            {portfolioRows.map(renderRow)}
          </ol>
        </>
      )}
    </section>
  );
};

export default PortfolioDonut;
